import React, { lazy, Suspense } from 'react';
import { render } from 'react-dom';

import { Skeleton } from '@zooplus/ui-components';

import { PayPalCheckoutButtonProps } from '@features/PayPal/PayPalCheckoutButton/PayPalCheckoutButton';
import KeycloakTokenProvider from '@providers/KeycloakTokenProvider';
import { getSentryService } from '@services/sentryService';
import { Locale } from '@types-internal';
import { getDefaultConfig } from '@utils/configUtils';
import { required } from '@utils/utils';

/* To prevent the bundle size from getting too big, we should use React lazy
 * for all entries. */
const PayPalCheckoutButton = lazy(
  () => import('@features/PayPal/PayPalCheckoutButton/PayPalCheckoutButton'),
);

const runningEnv = window.environment ?? 'dev';
const defaultConfig = getDefaultConfig();

export type RenderPayPalCheckoutButtonConfig = {
  onApprove: (payPalOrderId: string) => void;
  onCancel: () => void;
};

export const renderPayPalCheckoutButton = ({
  onApprove,
  onCancel,
}: RenderPayPalCheckoutButtonConfig) => {
  const container = document.getElementById('paypal-checkout-button-container');
  const sentryService = getSentryService(runningEnv, defaultConfig);

  if (container) {
    const { dataset } = container;
    try {
      const props: PayPalCheckoutButtonProps = {
        amountMajorUnit: required(dataset.amount, 'amount'),
        currency: required(dataset.currency, 'currency'),
        domain: dataset.domain,
        errorContainerId: dataset.errorContainerId,
        locale: dataset.locale?.replace('_', '-') as Locale,
        runningEnv: required(dataset.runningEnv, 'runningEnv'),
        shopBundle: required(dataset.shopBundle, 'shopBundle'),
        isCustomerRegistered: required(dataset.customerRegistered) === 'true',
        siteId: required(dataset.siteId, 'siteId'),
        userIdToken: dataset.customerRegistered
          ? dataset.userIdToken
          : required(dataset.userIdToken, 'userIdToken'),
        onApprove: required(onApprove, 'onApprove callback'),
        onCancel: required(onCancel, 'onCancel callback'),
      };

      render(
        <React.StrictMode>
          <Suspense fallback={<Skeleton />}>
            {props.isCustomerRegistered ? (
              <KeycloakTokenProvider allowedUrls={[defaultConfig.retryPaymentBaseUrl]}>
                <PayPalCheckoutButton {...props} />
              </KeycloakTokenProvider>
            ) : (
              <PayPalCheckoutButton {...props} />
            )}
          </Suspense>
        </React.StrictMode>,
        container,
      );
    } catch (error: any) {
      console.error('Error rendering PayPal checkout button', error);
      sentryService.captureException({
        message: 'Error occurs while rendering PayPal Checkout Button',
        error,
      });
    }
  }
};
